import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * Kontextuelle Hilfe-Tour fuer die Seite "Allgemeine Einstellungen"
 * (`/settings/general`). Wird ueber den `?`-Button im Settings-Sticky-Header
 * gestartet (Pattern analog `AVAILABILITY_CALENDAR_HELP_TOUR`).
 *
 * Acht Schritte — Fokus auf die fachlichen Hebel:
 *  1. Welcome (Spotlight auf company-info)
 *  2. Unternehmenstyp
 *  3. Organisationsstruktur (Abteilungen vs. Projekte)
 *  4. Land + Bundesland → Feiertagskatalog
 *  5. Geschaeftszeiten je Wochentag (Solver-Korridor)
 *  6. Urlaubs-Defaults
 *  7. Feiertags-Verhalten Default (Solver-Eingang)
 *  8. Kontaktdaten & Adresse (Wetter-Widget + Standort-Anker)
 *
 * Tour-Texte in `de.json/en.json` unter `TUTORIAL.SETTINGS_GENERAL.*`,
 * Voice-Over-Manifest unter `settings-general-help`.
 */
export const SETTINGS_GENERAL_HELP_TOUR: TourConfig = {
  id: TourFeature.SettingsGeneralHelp,
  version: 1,
  title: 'SETTINGS_GENERAL_HELP_TOUR_TITLE',
  description: 'SETTINGS_GENERAL_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  triggerContext: { route: '/settings/general' },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 130,
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'SETTINGS_GENERAL_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'welcome',
          anchor: AnchorId.CompanyInfo,
          spotlightAnchor: AnchorId.CompanyInfo,
          title: 'TUTORIAL.SETTINGS_GENERAL.WELCOME.TITLE',
          content: 'TUTORIAL.SETTINGS_GENERAL.WELCOME.CONTENT',
          placement: 'bottom',
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: false,
        },
        {
          id: 'company-type',
          anchor: AnchorId.CompanyTypeSelect,
          spotlightAnchor: AnchorId.CompanyTypeSelect,
          title: 'TUTORIAL.SETTINGS_GENERAL.COMPANY_TYPE.TITLE',
          content: 'TUTORIAL.SETTINGS_GENERAL.COMPANY_TYPE.CONTENT',
          placement: 'right',
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          id: 'work-structure',
          anchor: AnchorId.WorkStructureSelect,
          spotlightAnchor: AnchorId.WorkStructureSelect,
          title: 'TUTORIAL.SETTINGS_GENERAL.WORK_STRUCTURE.TITLE',
          content: 'TUTORIAL.SETTINGS_GENERAL.WORK_STRUCTURE.CONTENT',
          placement: 'right',
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          id: 'region',
          anchor: AnchorId.RegionSelect,
          spotlightAnchor: AnchorId.RegionalSettings,
          title: 'TUTORIAL.SETTINGS_GENERAL.REGION.TITLE',
          content: 'TUTORIAL.SETTINGS_GENERAL.REGION.CONTENT',
          placement: 'right',
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          id: 'business-hours',
          anchor: AnchorId.BusinessHoursPerDay,
          spotlightAnchor: AnchorId.CalendarSettings,
          title: 'TUTORIAL.SETTINGS_GENERAL.BUSINESS_HOURS.TITLE',
          content: 'TUTORIAL.SETTINGS_GENERAL.BUSINESS_HOURS.CONTENT',
          placement: 'top',
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          id: 'vacation',
          anchor: AnchorId.StandardVacationDays,
          spotlightAnchor: AnchorId.VacationSettings,
          title: 'TUTORIAL.SETTINGS_GENERAL.VACATION.TITLE',
          content: 'TUTORIAL.SETTINGS_GENERAL.VACATION.CONTENT',
          placement: 'right',
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          id: 'holiday-default',
          anchor: AnchorId.HolidayWorkModeDefault,
          spotlightAnchor: AnchorId.HolidayWorkModeDefault,
          title: 'TUTORIAL.SETTINGS_GENERAL.HOLIDAY_DEFAULT.TITLE',
          content: 'TUTORIAL.SETTINGS_GENERAL.HOLIDAY_DEFAULT.CONTENT',
          placement: 'top',
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          id: 'contact-address',
          // Kontaktdaten & Adresse — Section ist per Default eingeklappt.
          // `onBeforeShow` klickt den Collapse-Toggle, sodass das Akkordeon
          // beim Erreichen des Steps offen ist und der User die Adress-
          // felder sieht, von denen der Voice-Over spricht. Idempotent ueber
          // aria-expanded.
          anchor: AnchorId.ContactAddressSection,
          spotlightAnchor: AnchorId.ContactAddressSection,
          title: 'TUTORIAL.SETTINGS_GENERAL.CONTACT_ADDRESS.TITLE',
          content: 'TUTORIAL.SETTINGS_GENERAL.CONTACT_ADDRESS.CONTENT',
          placement: 'top',
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: true,
          onBeforeShow: () => {
            const section = document.querySelector(
              '[onboardingAnchor="contact-address-section"]'
            );
            const toggle = section?.querySelector<HTMLElement>(
              '.section-toggle, [class*="toggle"], button[aria-expanded]'
            );
            if (toggle && toggle.getAttribute('aria-expanded') !== 'true') {
              toggle.click();
            }
          },
        },
      ],
    },
  ],
};
